'use client';

import React, { useState } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import BookingModal from '@/app/search/components/BookingModal';

interface Property {
  id: number;
  name: string;
  location: string;
  type: string;
  rating: number;
  reviews: number;
  price: number;
  currency: string;
  image: string;
  alt: string;
  badge?: string;
  isSuperhost?: boolean;
  amenities: string[];
}

interface Attraction {
  name: string;
  category: string;
  description: string;
  image: string;
  alt: string;
  duration: string;
  distance: string;
}

interface DestinationData {
  city: string;
  country: string;
  tagline: string;
  description: string;
  heroImage: string;
  heroAlt: string;
  stats: { label: string; value: string }[];
  highlights: { icon: string; title: string; desc: string }[];
  featuredProperties: Property[];
  popularAccommodations: Property[];
  attractions: Attraction[];
}

const destinationData: Record<string, DestinationData> = {
  kampala: {
    city: 'Kampala',
    country: 'Uganda',
    tagline: 'The Pearl of Africa\'s vibrant capital',
    description:
      'Kampala is Uganda\'s sprawling, hilly capital — a city of contrasts where colonial-era architecture meets gleaming new towers, and street food vendors share pavements with rooftop bars. Spread across seven hills, it pulses with energy day and night. From the historic Kasubi Tombs to the buzzing Owino Market, every corner tells a story. Whether you\'re here for business, culture, or adventure, Kampala rewards the curious traveller.',
    heroImage: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=1600&q=80',
    heroAlt: 'Kampala city skyline at dusk with modern buildings and lush green hills',
    stats: [
      { label: 'Properties', value: '1,240+' },
      { label: 'Avg. rating', value: '4.8★' },
      { label: 'Avg. nightly rate', value: 'UGX 180K' },
      { label: 'Attractions', value: '60+' },
    ],
    highlights: [
      { icon: '🌿', title: 'Nature & Parks', desc: 'Entebbe Botanical Gardens and Mabamba Wetland are day-trip favourites.' },
      { icon: '🍽️', title: 'Food Scene', desc: 'Rolex street food, Ugandan matoke, and a thriving café culture.' },
      { icon: '🎭', title: 'Arts & Culture', desc: 'National Theatre, Ndere Cultural Centre, and vibrant local galleries.' },
      { icon: '🛍️', title: 'Shopping', desc: 'Craft Village, Garden City Mall, and the sprawling Owino Market.' },
    ],
    featuredProperties: [
      {
        id: 1,
        name: 'Acacia Luxury Apartments',
        location: 'Kololo, Kampala',
        type: 'Serviced Apartment',
        rating: 4.87,
        reviews: 124,
        price: 180000,
        currency: 'UGX',
        badge: 'Guest favourite',
        isSuperhost: true,
        amenities: ['Wi-Fi', 'Pool', 'Parking', 'AC'],
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
        alt: 'Acacia Luxury Apartments living area with panoramic Kampala skyline views',
      },
      {
        id: 3,
        name: 'Kampala Boutique Hotel',
        location: 'Nakasero, Kampala',
        type: 'Boutique Hotel',
        rating: 4.72,
        reviews: 98,
        price: 250000,
        currency: 'UGX',
        badge: 'Top rated',
        isSuperhost: false,
        amenities: ['Wi-Fi', 'Restaurant', 'Bar', 'Gym'],
        image: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=800&q=80',
        alt: 'Kampala Boutique Hotel elegant lobby with warm lighting and local artwork',
      },
      {
        id: 5,
        name: 'Kololo Hilltop Residence',
        location: 'Kololo, Kampala',
        type: 'Private Villa',
        rating: 4.93,
        reviews: 47,
        price: 420000,
        currency: 'UGX',
        isSuperhost: true,
        amenities: ['Wi-Fi', 'Pool', 'Garden', 'Chef'],
        image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80',
        alt: 'Kololo Hilltop Residence private pool surrounded by tropical gardens',
      },
    ],
    popularAccommodations: [
      {
        id: 6,
        name: 'Kampala Backpackers Hostel',
        location: 'Kabalagala, Kampala',
        type: 'Hostel',
        rating: 4.55,
        reviews: 312,
        price: 45000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Bar', 'Tours', 'Lockers'],
        image: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&q=80',
        alt: 'Kampala Backpackers Hostel common area with travellers socialising',
      },
      {
        id: 7,
        name: 'City Centre Guesthouse',
        location: 'Kampala Road',
        type: 'Guesthouse',
        rating: 4.41,
        reviews: 178,
        price: 85000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Breakfast', 'AC', 'Security'],
        image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800&q=80',
        alt: 'City Centre Guesthouse clean and comfortable room with city views',
      },
      {
        id: 8,
        name: 'Muyenga Lake View Apartment',
        location: 'Muyenga, Kampala',
        type: 'Apartment',
        rating: 4.68,
        reviews: 63,
        price: 130000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Lake View', 'Kitchen', 'Parking'],
        image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80',
        alt: 'Muyenga Lake View Apartment modern interior with views of Lake Victoria',
      },
      {
        id: 9,
        name: 'Ntinda Executive Suites',
        location: 'Ntinda, Kampala',
        type: 'Serviced Apartment',
        rating: 4.60,
        reviews: 89,
        price: 160000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Gym', 'AC', 'Workspace'],
        image: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80',
        alt: 'Ntinda Executive Suites modern workspace and living area',
      },
    ],
    attractions: [
      {
        name: 'Kasubi Tombs',
        category: 'Heritage',
        description: 'UNESCO World Heritage Site — the royal burial grounds of Buganda kings, a sacred and historically significant landmark.',
        image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&q=80',
        alt: 'Kasubi Tombs traditional thatched royal burial grounds in Kampala',
        duration: '1–2 hrs',
        distance: '5 km from centre',
      },
      {
        name: 'Ndere Cultural Centre',
        category: 'Culture',
        description: 'Vibrant performances of traditional Ugandan music, dance, and storytelling every Thursday, Friday, and Sunday evening.',
        image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600&q=80',
        alt: 'Ndere Cultural Centre outdoor amphitheatre with traditional dancers performing',
        duration: '2–3 hrs',
        distance: '8 km from centre',
      },
      {
        name: 'Uganda Museum',
        category: 'Museum',
        description: 'East Africa\'s oldest museum, housing archaeological finds, traditional instruments, and exhibits on Uganda\'s diverse cultures.',
        image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
        alt: 'Uganda Museum colonial-era building with traditional artefacts on display',
        duration: '1–2 hrs',
        distance: '3 km from centre',
      },
      {
        name: 'Owino Market',
        category: 'Shopping',
        description: 'One of East Africa\'s largest open-air markets — a sensory overload of fabrics, spices, electronics, and street food.',
        image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80',
        alt: 'Owino Market bustling stalls with colourful fabrics and fresh produce',
        duration: '1–3 hrs',
        distance: '1 km from centre',
      },
      {
        name: 'Mabamba Wetland',
        category: 'Nature',
        description: 'Ramsar-listed wetland and the best place in the world to spot the elusive Shoebill stork in its natural habitat.',
        image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=600&q=80',
        alt: 'Mabamba Wetland papyrus swamp with a shoebill stork standing in shallow water',
        duration: 'Half day',
        distance: '45 km from centre',
      },
      {
        name: 'Kampala Craft Village',
        category: 'Shopping',
        description: 'Curated artisan market with handmade jewellery, bark cloth art, wooden carvings, and authentic Ugandan crafts.',
        image: 'https://images.unsplash.com/photo-1611348586804-61bf6c080437?w=600&q=80',
        alt: 'Kampala Craft Village artisan stalls with handmade wooden carvings and jewellery',
        duration: '1–2 hrs',
        distance: '2 km from centre',
      },
    ],
  },
  entebbe: {
    city: 'Entebbe',
    country: 'Uganda',
    tagline: 'Gateway to Lake Victoria\'s island paradise',
    description:
      'Entebbe sits on a lush peninsula jutting into Lake Victoria — Africa\'s largest lake. Once Uganda\'s colonial capital, it retains a relaxed, leafy charm that contrasts sharply with Kampala\'s bustle. Home to Uganda\'s international airport, world-famous botanical gardens, and the gateway to the Ssese Islands, Entebbe is both a practical base and a destination in its own right.',
    heroImage: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=1600&q=80',
    heroAlt: 'Entebbe lakeside at sunset with Lake Victoria stretching to the horizon',
    stats: [
      { label: 'Properties', value: '380+' },
      { label: 'Avg. rating', value: '4.9★' },
      { label: 'Avg. nightly rate', value: 'UGX 220K' },
      { label: 'Attractions', value: '25+' },
    ],
    highlights: [
      { icon: '🦜', title: 'Wildlife', desc: 'Uganda Wildlife Education Centre and chimpanzee sanctuary.' },
      { icon: '🌊', title: 'Lake Activities', desc: 'Boat trips to Ssese Islands, fishing, and sunset cruises.' },
      { icon: '🌺', title: 'Botanical Gardens', desc: 'Colonial-era gardens with rare tropical plants and bird species.' },
      { icon: '✈️', title: 'Airport Access', desc: 'Just 5 minutes from Entebbe International Airport.' },
    ],
    featuredProperties: [
      {
        id: 2,
        name: 'Entebbe Lakeside Villa',
        location: 'Entebbe',
        type: 'Villa',
        rating: 4.95,
        reviews: 89,
        price: 350000,
        currency: 'UGX',
        badge: 'Superhost',
        isSuperhost: true,
        amenities: ['Wi-Fi', 'Lake View', 'Garden', 'BBQ'],
        image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80',
        alt: 'Entebbe Lakeside Villa exterior with Lake Victoria views and tropical garden',
      },
      {
        id: 10,
        name: 'Airport Garden Hotel',
        location: 'Entebbe',
        type: 'Hotel',
        rating: 4.65,
        reviews: 201,
        price: 195000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Pool', 'Restaurant', 'Shuttle'],
        image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80',
        alt: 'Airport Garden Hotel pool area surrounded by lush tropical gardens',
      },
      {
        id: 11,
        name: 'Botanical Beach Retreat',
        location: 'Entebbe',
        type: 'Boutique Hotel',
        rating: 4.82,
        reviews: 55,
        price: 280000,
        currency: 'UGX',
        isSuperhost: true,
        amenities: ['Wi-Fi', 'Beach', 'Kayaks', 'Breakfast'],
        image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80',
        alt: 'Botanical Beach Retreat private beach on Lake Victoria with kayaks',
      },
    ],
    popularAccommodations: [
      {
        id: 12,
        name: 'Entebbe Backpackers',
        location: 'Entebbe',
        type: 'Hostel',
        rating: 4.48,
        reviews: 145,
        price: 40000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Garden', 'Tours', 'Bar'],
        image: 'https://images.unsplash.com/photo-1555854877-bab0e564b8d5?w=800&q=80',
        alt: 'Entebbe Backpackers garden common area with hammocks and travellers',
      },
      {
        id: 13,
        name: 'Lake View Guesthouse',
        location: 'Entebbe',
        type: 'Guesthouse',
        rating: 4.55,
        reviews: 92,
        price: 95000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Lake View', 'Breakfast', 'Parking'],
        image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800&q=80',
        alt: 'Lake View Guesthouse room with balcony overlooking Lake Victoria',
      },
      {
        id: 14,
        name: 'Entebbe Serviced Apartments',
        location: 'Entebbe',
        type: 'Apartment',
        rating: 4.70,
        reviews: 38,
        price: 150000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Kitchen', 'AC', 'Parking'],
        image: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80',
        alt: 'Entebbe Serviced Apartments modern kitchen and open-plan living area',
      },
      {
        id: 15,
        name: 'Protea Hotel Entebbe',
        location: 'Entebbe',
        type: 'Hotel',
        rating: 4.60,
        reviews: 267,
        price: 210000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Pool', 'Gym', 'Restaurant'],
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
        alt: 'Protea Hotel Entebbe outdoor pool with lake views in the background',
      },
    ],
    attractions: [
      {
        name: 'Uganda Wildlife Education Centre',
        category: 'Wildlife',
        description: 'Home to lions, giraffes, chimpanzees, and over 130 bird species — a conservation centre and family favourite.',
        image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&q=80',
        alt: 'Uganda Wildlife Education Centre with giraffes and tropical vegetation',
        duration: '2–3 hrs',
        distance: '2 km from centre',
      },
      {
        name: 'Entebbe Botanical Gardens',
        category: 'Nature',
        description: 'Established in 1898, these colonial-era gardens feature rare tropical plants, primates, and over 100 bird species.',
        image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600&q=80',
        alt: 'Entebbe Botanical Gardens lush tropical trees and flowering plants along Lake Victoria',
        duration: '1–2 hrs',
        distance: '1 km from centre',
      },
      {
        name: 'Ssese Islands Day Trip',
        category: 'Island',
        description: 'A short ferry ride to 84 tropical islands on Lake Victoria — pristine beaches, fishing villages, and forest walks.',
        image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
        alt: 'Ssese Islands tropical beach on Lake Victoria with palm trees and clear water',
        duration: 'Full day',
        distance: '2 hrs by ferry',
      },
      {
        name: 'Lake Victoria Sunset Cruise',
        category: 'Experience',
        description: 'Guided boat cruise on Africa\'s largest lake — spot fish eagles, hippos, and watch the sun dip below the horizon.',
        image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=600&q=80',
        alt: 'Sunset cruise on Lake Victoria with golden reflections on calm water',
        duration: '2 hrs',
        distance: 'Entebbe Pier',
      },
    ],
  },
  jinja: {
    city: 'Jinja',
    country: 'Uganda',
    tagline: 'Adventure capital at the source of the Nile',
    description:
      'Jinja is East Africa\'s undisputed adventure capital, perched at the source of the mighty Nile River. White-water rafting, bungee jumping, kayaking, and quad biking draw thrill-seekers from around the world. But Jinja is more than adrenaline — it\'s a charming colonial town with a vibrant café scene, craft markets, and the spiritual significance of being where the world\'s longest river begins its 6,650 km journey to the Mediterranean.',
    heroImage: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=1600&q=80',
    heroAlt: 'Jinja source of the Nile with white-water rapids and lush green riverbanks',
    stats: [
      { label: 'Properties', value: '290+' },
      { label: 'Avg. rating', value: '4.8★' },
      { label: 'Avg. nightly rate', value: 'UGX 160K' },
      { label: 'Attractions', value: '30+' },
    ],
    highlights: [
      { icon: '🌊', title: 'White-water Rafting', desc: 'Grade 5 rapids on the Nile — one of the world\'s best rafting experiences.' },
      { icon: '🪂', title: 'Bungee Jumping', desc: 'Leap 44 metres over the Nile at Adrift Bungee.' },
      { icon: '🚣', title: 'Kayaking & SUP', desc: 'Paddle the calm upper Nile past hippos and kingfishers.' },
      { icon: '🏍️', title: 'Quad Biking', desc: 'Off-road trails through sugarcane fields and Nile-side villages.' },
    ],
    featuredProperties: [
      {
        id: 4,
        name: 'Jinja Nile View Lodge',
        location: 'Jinja',
        type: 'Lodge',
        rating: 4.78,
        reviews: 203,
        price: 220000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'River View', 'Restaurant', 'Tours'],
        image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&q=80',
        alt: 'Jinja Nile View Lodge terrace overlooking the source of the Nile at sunset',
      },
      {
        id: 16,
        name: 'Nile River Camp',
        location: 'Jinja',
        type: 'Eco Camp',
        rating: 4.85,
        reviews: 167,
        price: 180000,
        currency: 'UGX',
        badge: 'Guest favourite',
        isSuperhost: true,
        amenities: ['Wi-Fi', 'Nile View', 'Rafting', 'Campfire'],
        image: 'https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=800&q=80',
        alt: 'Nile River Camp luxury tents on the banks of the Nile with campfire area',
      },
      {
        id: 17,
        name: 'Jinja Backpackers & Resort',
        location: 'Jinja',
        type: 'Resort',
        rating: 4.60,
        reviews: 289,
        price: 95000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Pool', 'Bar', 'Adventure Tours'],
        image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800&q=80',
        alt: 'Jinja Backpackers Resort pool area with travellers relaxing by the Nile',
      },
    ],
    popularAccommodations: [
      {
        id: 18,
        name: 'Source of the Nile Hotel',
        location: 'Jinja',
        type: 'Hotel',
        rating: 4.50,
        reviews: 134,
        price: 140000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Restaurant', 'Bar', 'Parking'],
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80',
        alt: 'Source of the Nile Hotel colonial-style building with manicured gardens',
      },
      {
        id: 19,
        name: 'Jinja Town Guesthouse',
        location: 'Jinja',
        type: 'Guesthouse',
        rating: 4.38,
        reviews: 76,
        price: 65000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Breakfast', 'AC', 'Security'],
        image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=800&q=80',
        alt: 'Jinja Town Guesthouse clean room with local artwork and comfortable furnishings',
      },
      {
        id: 20,
        name: 'Adrift Adventure Lodge',
        location: 'Jinja',
        type: 'Lodge',
        rating: 4.72,
        reviews: 198,
        price: 200000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Nile View', 'Rafting Packages', 'Pool'],
        image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800&q=80',
        alt: 'Adrift Adventure Lodge riverside deck with views of the Nile rapids',
      },
      {
        id: 21,
        name: 'Nile Porch Apartments',
        location: 'Jinja',
        type: 'Apartment',
        rating: 4.55,
        reviews: 42,
        price: 110000,
        currency: 'UGX',
        amenities: ['Wi-Fi', 'Kitchen', 'Balcony', 'Parking'],
        image: 'https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=800&q=80',
        alt: 'Nile Porch Apartments modern living space with balcony views of the Nile',
      },
    ],
    attractions: [
      {
        name: 'White-water Rafting',
        category: 'Adventure',
        description: 'Tackle Grade 5 rapids on the Nile with experienced guides — one of the world\'s top rafting destinations.',
        image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=600&q=80',
        alt: 'White-water rafting on the Nile with a group navigating powerful rapids',
        duration: 'Full day',
        distance: 'Bujagali Falls',
      },
      {
        name: 'Source of the Nile',
        category: 'Heritage',
        description: 'Visit the exact spot where the Nile begins its 6,650 km journey — a monument marks the historic location.',
        image: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600&q=80',
        alt: 'Source of the Nile monument at Jinja with the river flowing in the background',
        duration: '1 hr',
        distance: '3 km from centre',
      },
      {
        name: 'Bungee Jumping',
        category: 'Adventure',
        description: 'Leap 44 metres over the Nile at Adrift Bungee — one of Africa\'s most spectacular bungee locations.',
        image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80',
        alt: 'Bungee jumping platform over the Nile River at Jinja with lush green banks',
        duration: '2 hrs',
        distance: 'Bujagali Falls',
      },
      {
        name: 'Nile Kayaking',
        category: 'Water Sports',
        description: 'Paddle the calm upper Nile past hippos, kingfishers, and local fishing villages on a guided kayak tour.',
        image: 'https://images.unsplash.com/photo-1580060839134-75a5edca2e99?w=600&q=80',
        alt: 'Kayaking on the calm upper Nile with lush green vegetation on the riverbanks',
        duration: '3 hrs',
        distance: 'Jinja Pier',
      },
    ],
  },
};

// Fallback for unknown slugs — use Kampala data
function getDestinationData(slug: string): DestinationData {
  const key = slug.toLowerCase().replace(/-/g, ' ').replace(/\s+/g, '');
  return destinationData[key] || destinationData['kampala'];
}

function formatPrice(price: number, currency: string) {
  return `${currency} ${price.toLocaleString()}`;
}

const categoryColors: Record<string, string> = {
  Heritage: 'bg-amber-100 text-amber-800',
  Culture: 'bg-purple-100 text-purple-800',
  Museum: 'bg-blue-100 text-blue-800',
  Shopping: 'bg-blue-100 text-blue-800',
  Nature: 'bg-green-100 text-green-800',
  Wildlife: 'bg-orange-100 text-orange-800',
  Island: 'bg-cyan-100 text-cyan-800',
  Experience: 'bg-indigo-100 text-indigo-800',
  Adventure: 'bg-amber-100 text-amber-800',
  'Water Sports': 'bg-sky-100 text-sky-800',
};

interface PropertyCardProps {
  property: Property;
  onBook: (property: Property) => void;
}

function PropertyCard({ property, onBook }: PropertyCardProps) {
  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300">
      <div className="relative aspect-[4/3] overflow-hidden">
        <AppImage
          src={property.image}
          alt={property.alt}
          fill
          className="object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {property.badge && (
          <span className="absolute top-3 left-3 bg-white text-gray-900 text-xs font-semibold px-2.5 py-1 rounded-full shadow-sm">
            {property.badge}
          </span>
        )}
        {property.isSuperhost && (
          <span className="absolute top-3 right-3 bg-[#4088C6] text-white text-xs font-semibold px-2.5 py-1 rounded-full">
            Superhost
          </span>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-1">{property.name}</h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            <svg className="w-3.5 h-3.5 text-amber-400 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
            <span className="text-xs font-medium text-gray-700">{property.rating}</span>
            <span className="text-xs text-gray-400">({property.reviews})</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-1">{property.location} · {property.type}</p>
        <div className="flex flex-wrap gap-1 mb-3">
          {property.amenities.slice(0, 3).map((a) => (
            <span key={a} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{a}</span>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="font-bold text-gray-900 text-sm">{formatPrice(property.price, property.currency)}</span>
            <span className="text-xs text-gray-400"> / night</span>
          </div>
          <div className="flex gap-2">
            <Link
              href={`/property/${property.id}`}
              className="text-xs font-medium text-[#4088C6] hover:text-[#3070a6] underline underline-offset-2"
            >
              View
            </Link>
            <button
              onClick={() => onBook(property)}
              className="text-xs font-semibold bg-[#4088C6] hover:bg-[#3070a6] text-white px-3 py-1.5 rounded-lg transition-colors"
            >
              Reserve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DestinationPage() {
  const params = useParams();
  const slug = (params?.slug as string) || 'kampala';
  const data = getDestinationData(slug);
  const [bookingProperty, setBookingProperty] = useState<Property | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[400px] w-full overflow-hidden">
        <AppImage
          src={data.heroImage}
          alt={data.heroAlt}
          fill
          className="object-cover"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        {/* Breadcrumb */}
        <div className="absolute top-24 left-0 right-0 px-4 sm:px-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-white/80 text-sm">
            <Link href="/home" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <span className="text-white font-medium">{data.city}</span>
          </div>
        </div>
        {/* Hero content */}
        <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-10 max-w-7xl mx-auto">
          <p className="text-white/80 text-sm font-medium uppercase tracking-widest mb-2">{data.country}</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2">{data.city}</h1>
          <p className="text-white/90 text-lg">{data.tagline}</p>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-[#4088C6] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {data.stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-xl font-bold">{stat.value}</p>
              <p className="text-white/80 text-xs mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 space-y-16">

        {/* Description + Highlights */}
        <section className="grid md:grid-cols-5 gap-10">
          <div className="md:col-span-3">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About {data.city}</h2>
            <p className="text-gray-600 leading-relaxed text-base">{data.description}</p>
          </div>
          <div className="md:col-span-2 grid grid-cols-2 gap-4">
            {data.highlights.map((h) => (
              <div key={h.title} className="bg-gray-50 rounded-2xl p-4">
                <span className="text-2xl mb-2 block">{h.icon}</span>
                <p className="font-semibold text-gray-900 text-sm mb-1">{h.title}</p>
                <p className="text-gray-500 text-xs leading-relaxed">{h.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Featured Properties */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Featured properties</h2>
              <p className="text-gray-500 mt-1 text-sm">Top-rated stays in {data.city}</p>
            </div>
            <Link
              href={`/search?q=${data.city}`}
              className="text-sm font-semibold text-[#4088C6] hover:text-[#3070a6] flex items-center gap-1"
            >
              View all
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.featuredProperties.map((p) => (
              <PropertyCard key={p.id} property={p} onBook={setBookingProperty} />
            ))}
          </div>
        </section>

        {/* Popular Accommodations */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Popular accommodations</h2>
              <p className="text-gray-500 mt-1 text-sm">Well-loved stays across all budgets</p>
            </div>
            <Link
              href={`/search?q=${data.city}`}
              className="text-sm font-semibold text-[#4088C6] hover:text-[#3070a6] flex items-center gap-1"
            >
              View all
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.popularAccommodations.map((p) => (
              <PropertyCard key={p.id} property={p} onBook={setBookingProperty} />
            ))}
          </div>
        </section>

        {/* Attractions */}
        <section>
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Things to do in {data.city}</h2>
            <p className="text-gray-500 mt-1 text-sm">Experiences and attractions worth exploring</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.attractions.map((attraction) => (
              <div key={attraction.name} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300">
                <div className="relative h-44 overflow-hidden">
                  <AppImage
                    src={attraction.image}
                    alt={attraction.alt}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className={`absolute top-3 left-3 text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColors[attraction.category] || 'bg-gray-100 text-gray-700'}`}>
                    {attraction.category}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">{attraction.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{attraction.description}</p>
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      {attraction.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      {attraction.distance}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-3xl p-8 sm:p-12 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">Ready to visit {data.city}?</h2>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">Browse all available properties and find the perfect stay for your trip.</p>
          <Link
            href={`/search?q=${data.city}`}
            className="inline-flex items-center gap-2 bg-[#4088C6] hover:bg-[#3070a6] text-white font-semibold px-8 py-3.5 rounded-xl transition-colors text-sm"
          >
            Search stays in {data.city}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </Link>
        </section>

      </main>

      <Footer />

      {/* Booking Modal */}
      {bookingProperty && (
        <BookingModal
          onClose={() => setBookingProperty(null)}
          property={{
            id: bookingProperty.id,
            name: bookingProperty.name,
            location: bookingProperty.location,
            country: 'Uganda',
            type: bookingProperty.type,
            price: bookingProperty.price,
            currency: bookingProperty.currency,
            images: [{ src: bookingProperty.image, alt: bookingProperty.alt }],
            rating: bookingProperty.rating,
            reviews: bookingProperty.reviews,
          }}
        />
      )}
    </div>
  );
}
